import React, { useState } from 'react';
import './ProvenanceBadge.css';

/**
 * Calculates freshness based on field TTL (emails: 7 days, other fields: 30 days)
 */
export const calculateFieldFreshness = (fieldName, lastVerifiedAt) => {
  if (!lastVerifiedAt) return { isStale: false, daysOld: 0, ttlDays: 30 };
  const date = new Date(lastVerifiedAt);
  if (isNaN(date.getTime())) return { isStale: false, daysOld: 0, ttlDays: 30 };

  const daysOld = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
  const isEmail = fieldName?.toLowerCase().includes('email');
  const ttlDays = isEmail ? 7 : 30;
  const isStale = daysOld > ttlDays;

  return { isStale, daysOld, ttlDays };
};

/**
 * ProvenanceBadge component
 * Renders source, confidence score, lock state, stale warnings, and conflict flags.
 */
const ProvenanceBadge = ({
  fieldName,
  fieldLabel,
  currentValue,
  lockInfo = {},
  provenanceInfo = {},
  onLockClick,
  onUnlockClick,
  onOpenDrawer,
  canEdit = true,
  compact = false,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const isLocked = Boolean(lockInfo?.isLocked);
  const lockReason = lockInfo?.reason || '';
  const lockedBy = lockInfo?.lockedBy || 'Team member';
  const lockedAt = lockInfo?.lockedAt
    ? new Date(lockInfo.lockedAt).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      })
    : null;

  const provenanceClass =
    provenanceInfo?.provenanceClass || (isLocked ? 'user_verified' : 'extracted');
  const source = provenanceInfo?.source || (isLocked ? 'User Verified' : 'System');
  const confidence =
    provenanceInfo?.confidence != null ? Math.round(provenanceInfo.confidence) : null;
  const lastVerifiedAt = provenanceInfo?.lastVerifiedAt || lockInfo?.lockedAt;
  const hasConflict = Boolean(provenanceInfo?.hasConflict);

  const { isStale, daysOld, ttlDays } = calculateFieldFreshness(fieldName, lastVerifiedAt);

  const getSourceClass = () => {
    switch (provenanceClass) {
      case 'user_verified':
        return 'prov-class-verified';
      case 'provider_supplied':
        return 'prov-class-provider';
      case 'extracted':
        return 'prov-class-extracted';
      case 'inferred':
        return 'prov-class-inferred';
      default:
        return 'prov-class-default';
    }
  };

  const handleBadgeClick = (e) => {
    e.stopPropagation();
    if (onOpenDrawer) {
      onOpenDrawer('ledger', fieldName);
    }
  };

  const handleConflictClick = (e) => {
    e.stopPropagation();
    if (onOpenDrawer) {
      onOpenDrawer('conflicts', fieldName);
    }
  };

  const handleQuickLock = (e) => {
    e.stopPropagation();
    if (onLockClick) {
      onLockClick(fieldName, fieldLabel || fieldName, currentValue);
    }
  };

  const handleQuickUnlock = (e) => {
    e.stopPropagation();
    if (
      window.confirm(
        `Unlock ${fieldLabel || fieldName}? Background syncs will be able to update it.`
      )
    ) {
      onUnlockClick(fieldName);
    }
  };

  return (
    <div
      className={`provenance-badge-container ${compact ? 'compact' : ''}`}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {/* 1. Locked State Badge */}
      {isLocked ? (
        <span
          className="prov-badge prov-badge-locked"
          onClick={handleBadgeClick}
          title={`Click to view ledger. Locked by ${lockedBy}: "${lockReason}"`}
        >
          <i className="fas fa-lock prov-icon" />
          <span className="prov-text">{compact ? 'Locked' : `User Verified • 🔒 Locked`}</span>
          {canEdit && (
            <button
              type="button"
              className="prov-inline-unlock-btn"
              onClick={handleQuickUnlock}
              title="Unlock this field"
            >
              <i className="fas fa-times" />
            </button>
          )}
        </span>
      ) : (
        /* 2. Unlocked Provenance Badge */
        <span
          className={`prov-badge ${getSourceClass()}`}
          onClick={handleBadgeClick}
          title="Click to view evidence history"
        >
          <span className="prov-source">{source}</span>
          {confidence !== null && <span className="prov-confidence"> • {confidence}%</span>}
          {!isStale && <span className="prov-fresh-dot" title="Fresh" />}
        </span>
      )}

      {/* 3. Stale Alert Badge */}
      {isStale && (
        <span
          className="prov-badge prov-badge-stale"
          onClick={handleBadgeClick}
          title={`⚠️ Stale Alert: Last verified ${daysOld}d ago (exceeds ${ttlDays}d TTL). Re-verify to avoid bounce.`}
        >
          <span className="prov-stale-icon">⚠️</span>
          <span className="prov-stale-text">Stale ({daysOld}d)</span>
        </span>
      )}

      {/* 4. Conflict Warning Pill */}
      {hasConflict && (
        <button
          type="button"
          className="prov-badge prov-badge-conflict pulse"
          onClick={handleConflictClick}
          title="⚡ Provider conflict detected! Click to review competing values in Evidence Drawer"
        >
          <i className="fas fa-bolt" /> Conflict
        </button>
      )}

      {/* 5. Quick Lock Action Button for Reps (when not locked) */}
      {!isLocked && canEdit && onLockClick && (
        <button
          type="button"
          className="prov-lock-action-btn"
          onClick={handleQuickLock}
          title={`Lock ${fieldLabel || fieldName} to prevent automated sync overwrites`}
        >
          <i className="fas fa-lock-open prov-lock-icon" />
          <span className="prov-lock-hover-text">Lock</span>
        </button>
      )}

      {/* Popover Tooltip with Details */}
      {showTooltip && (
        <div className="prov-tooltip-popover">
          <div className="prov-tooltip-header">
            <strong>{fieldLabel || fieldName} Provenance</strong>
            {isLocked && <span className="prov-tag-locked">Protected</span>}
          </div>
          <div className="prov-tooltip-body">
            <div>
              <span className="prov-tip-label">Source:</span> {source} (
              {provenanceClass.replace('_', ' ')})
            </div>
            {confidence !== null && (
              <div>
                <span className="prov-tip-label">Confidence:</span> {confidence}%
              </div>
            )}
            {lastVerifiedAt && (
              <div>
                <span className="prov-tip-label">Verified:</span>{' '}
                {new Date(lastVerifiedAt).toLocaleDateString()} ({daysOld}d ago)
              </div>
            )}
            {isLocked && (
              <div className="prov-tooltip-lock-details">
                <div>
                  <span className="prov-tip-label">Locked by:</span> {lockedBy}{' '}
                  {lockedAt ? `on ${lockedAt}` : ''}
                </div>
                <div>
                  <span className="prov-tip-label">Note:</span> <em>"{lockReason}"</em>
                </div>
              </div>
            )}
            {isStale && (
              <div className="prov-tooltip-stale-warn">
                ⚠️ Exceeded {ttlDays}-day verification TTL. Re-verify before sending outreach.
              </div>
            )}
            {hasConflict && (
              <div className="prov-tooltip-conflict-warn">
                ⚡ An external provider returned new conflicting data. Click badge to resolve.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProvenanceBadge;
